import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Alert, Form, Spinner, Stack } from 'react-bootstrap';
import Button from 'components/base/Button';
import { ConfigConstants } from 'utils/config';
import { captureVideoFrame, waitForBlink } from 'utils/faceBlinkDetector';
import { formatAttendanceTime } from 'utils/workSchedule';

const DEVICE_TOKEN_KEY = 'faceClockDeviceToken';
const DEVICE_REFRESH_KEY = 'faceClockRefreshToken';
const DEVICE_EMAIL_KEY = 'faceClockDeviceEmail';
const DEVICE_EXPIRES_AT_KEY = 'faceClockExpiresAt';
const DEFAULT_DEVICE_EMAIL = 'biometrics@kjauto.ph';
const TIMEZONE_OFFSET_HOURS = 8;
/** Refresh a few minutes before access token expiry (expiresIn is minutes). */
const REFRESH_SKEW_MS = 5 * 60 * 1000;
const DEFAULT_EXPIRES_IN_MINUTES = 60;

type AttendanceAction = 'TimeIn' | 'TimeOut';

type ClockResult = {
  userId: number;
  displayName: string;
  attendanceLogType: 'TimeIn' | 'TimeOut' | number;
  matchScore: number;
  timestamp: string;
};

/** Home = choose Time In/Out. Capture = camera + blink. Result = brief success/error. */
type Phase =
  | { kind: 'home' }
  | { kind: 'capture'; action: AttendanceAction; prompt: string }
  | { kind: 'success'; result: ClockResult }
  | { kind: 'error'; message: string };

/** Prefer localStorage so a kiosk phone keeps the device signed in across reloads. */
function readDeviceStorage(key: string): string | null {
  const fromLocal = localStorage.getItem(key);
  if (fromLocal) return fromLocal;
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) {
    localStorage.setItem(key, fromSession);
    sessionStorage.removeItem(key);
    return fromSession;
  }
  return null;
}

function clearDeviceStorage() {
  for (const store of [localStorage, sessionStorage]) {
    store.removeItem(DEVICE_TOKEN_KEY);
    store.removeItem(DEVICE_REFRESH_KEY);
    store.removeItem(DEVICE_EMAIL_KEY);
    store.removeItem(DEVICE_EXPIRES_AT_KEY);
  }
}

const FaceClock = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const resetTimerRef = useRef<number | null>(null);
  const blinkAbortRef = useRef<AbortController | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null);
  const deviceTokenRef = useRef<string | null>(readDeviceStorage(DEVICE_TOKEN_KEY));
  const [deviceToken, setDeviceToken] = useState<string | null>(
    () => deviceTokenRef.current
  );
  const [deviceEmail, setDeviceEmail] = useState(
    () => readDeviceStorage(DEVICE_EMAIL_KEY) || DEFAULT_DEVICE_EMAIL
  );
  const [password, setPassword] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'home' });

  const stopMediaTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const stopCamera = useCallback(() => {
    blinkAbortRef.current?.abort();
    blinkAbortRef.current = null;
    stopMediaTracks();
  }, [stopMediaTracks]);

  const goHome = useCallback(() => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    stopCamera();
    setPhase({ kind: 'home' });
  }, [stopCamera]);

  const clearDeviceSession = useCallback(() => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    clearDeviceStorage();
    deviceTokenRef.current = null;
    setDeviceToken(null);
    setPassword('');
    goHome();
  }, [goHome]);

  const persistDeviceSession = useCallback(
    (
      accessToken: string,
      refreshToken?: string,
      email?: string,
      expiresInMinutes?: number,
      options?: { syncReactState?: boolean }
    ) => {
      localStorage.setItem(DEVICE_TOKEN_KEY, accessToken);
      if (refreshToken) {
        localStorage.setItem(DEVICE_REFRESH_KEY, refreshToken);
      }
      if (email) {
        localStorage.setItem(DEVICE_EMAIL_KEY, email);
        setDeviceEmail(email);
      }
      const minutes =
        typeof expiresInMinutes === 'number' && expiresInMinutes > 0
          ? expiresInMinutes
          : DEFAULT_EXPIRES_IN_MINUTES;
      localStorage.setItem(
        DEVICE_EXPIRES_AT_KEY,
        String(Date.now() + minutes * 60 * 1000)
      );
      deviceTokenRef.current = accessToken;
      if (options?.syncReactState !== false) {
        setDeviceToken(accessToken);
      }
      setLoginError(null);
    },
    []
  );

  const refreshDeviceSession = useCallback(async (): Promise<string | null> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const run = (async () => {
      const refreshToken = readDeviceStorage(DEVICE_REFRESH_KEY);
      if (!refreshToken) return null;

      try {
        const res = await axios.post(`${ConfigConstants.baseUrl}token`, {
          refreshToken,
          grantType: 'refresh_token'
        });
        persistDeviceSession(
          res.data.accessToken,
          res.data.refreshToken ?? refreshToken,
          readDeviceStorage(DEVICE_EMAIL_KEY) || undefined,
          res.data.expiresIn,
          { syncReactState: false }
        );
        return res.data.accessToken as string;
      } catch {
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = run;
    return run;
  }, [persistDeviceSession]);

  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiresAtRaw = localStorage.getItem(DEVICE_EXPIRES_AT_KEY);
    const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : NaN;
    const delayMs = Number.isFinite(expiresAt)
      ? Math.max(15_000, expiresAt - Date.now() - REFRESH_SKEW_MS)
      : Math.max(
          15_000,
          DEFAULT_EXPIRES_IN_MINUTES * 60 * 1000 - REFRESH_SKEW_MS
        );

    refreshTimerRef.current = window.setTimeout(() => {
      void refreshDeviceSession().then(token => {
        if (token) {
          scheduleTokenRefresh();
          return;
        }
        refreshTimerRef.current = window.setTimeout(() => {
          scheduleTokenRefresh();
        }, 60_000);
      });
    }, delayMs);
  }, [refreshDeviceSession]);

  const openCamera = useCallback(async (): Promise<HTMLVideoElement> => {
    // Only release media — do not abort the in-flight attendance controller.
    stopMediaTracks();
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(
        'Camera API is unavailable. Open this page in Chrome or Edge (not an embedded preview).'
      );
    }

    const waitForVideoElement = async (): Promise<HTMLVideoElement> => {
      const started = performance.now();
      while (performance.now() - started < 3000) {
        if (videoRef.current) return videoRef.current;
        await new Promise(r => window.setTimeout(r, 16));
      }
      throw new Error('Camera view is not ready');
    };

    const attempts: MediaStreamConstraints[] = [
      { audio: false, video: { facingMode: { ideal: 'user' } } },
      { audio: false, video: true }
    ];

    let lastError: unknown;
    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        const video = await waitForVideoElement();
        video.srcObject = stream;
        video.muted = true;
        video.setAttribute('playsinline', 'true');
        await video.play().catch(() => undefined);

        const started = performance.now();
        while (video.readyState < 2 && performance.now() - started < 5000) {
          await new Promise(r => window.setTimeout(r, 50));
        }
        if (video.readyState < 2) {
          throw new Error('Camera is not ready yet');
        }

        return video;
      } catch (error) {
        lastError = error;
        stopMediaTracks();
      }
    }

    const err = lastError as DOMException | Error | undefined;
    const name = err && 'name' in err ? err.name : '';
    const detail = err?.message || 'Unknown camera error';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      throw new Error(
        'Camera permission was blocked. Allow camera for this site, then try again.'
      );
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      throw new Error(
        'Camera is in use by another app. Close other camera apps, or open this page in Chrome/Edge.'
      );
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      throw new Error('No camera was found on this device.');
    }
    throw new Error(
      `Could not open the camera (${name || 'Error'}: ${detail}).`
    );
  }, [stopMediaTracks]);

  const handleDeviceLogin = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (loginBusy) return;

    setLoginBusy(true);
    setLoginError(null);
    try {
      const res = await axios.post(`${ConfigConstants.baseUrl}token`, {
        email: deviceEmail.trim(),
        password,
        grantType: 'password'
      });
      persistDeviceSession(
        res.data.accessToken,
        res.data.refreshToken,
        deviceEmail.trim().toLowerCase(),
        res.data.expiresIn
      );
      setPassword('');
      setPhase({ kind: 'home' });
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.errorMessage || 'Device login failed'
          : 'Device login failed';
      setLoginError(message);
    } finally {
      setLoginBusy(false);
    }
  };

  useEffect(() => {
    if (!deviceToken) return;

    void refreshDeviceSession().finally(() => {
      scheduleTokenRefresh();
    });

    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [deviceToken, refreshDeviceSession, scheduleTokenRefresh]);

  useEffect(() => {
    return () => {
      stopCamera();
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, [stopCamera]);

  const actionLabel = (action: AttendanceAction) =>
    action === 'TimeOut' ? 'Time Out' : 'Time In';

  const scheduleReturnHome = (delayMs: number) => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout(() => {
      goHome();
    }, delayMs);
  };

  const postClock = async (
    blob: Blob,
    accessToken: string,
    attendanceLogType: AttendanceAction
  ) => {
    const formData = new FormData();
    formData.append('Images', blob, `face-clock-${Date.now()}.jpg`);
    formData.append('TimeZone', String(TIMEZONE_OFFSET_HOURS));
    formData.append('ClientLiveness', 'Blink');
    formData.append('AttendanceLogType', attendanceLogType);

    return axios.post(
      `${ConfigConstants.baseUrl}api/v1/faceRecognition/clock`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  };

  const startAttendance = async (attendanceLogType: AttendanceAction) => {
    if (!deviceTokenRef.current || phase.kind === 'capture') return;

    const label = actionLabel(attendanceLogType);
    blinkAbortRef.current?.abort();
    const abort = new AbortController();
    blinkAbortRef.current = abort;

    setPhase({
      kind: 'capture',
      action: attendanceLogType,
      prompt: `${label} — opening camera…`
    });

    try {
      const video = await openCamera();
      if (abort.signal.aborted) return;

      setPhase({
        kind: 'capture',
        action: attendanceLogType,
        prompt: `${label} — look at the camera, then blink`
      });

      await waitForBlink(video, {
        signal: abort.signal,
        onStatus: message =>
          setPhase({
            kind: 'capture',
            action: attendanceLogType,
            prompt: `${label} — ${message}`
          })
      });

      setPhase({
        kind: 'capture',
        action: attendanceLogType,
        prompt: `${label} — capturing…`
      });
      const blob = await captureVideoFrame(video);

      setPhase({
        kind: 'capture',
        action: attendanceLogType,
        prompt: `${label} — verifying…`
      });

      let accessToken = deviceTokenRef.current;
      if (!accessToken) {
        throw new Error('Device is not signed in');
      }

      let res;
      try {
        res = await postClock(blob, accessToken, attendanceLogType);
      } catch (error) {
        if (!(error instanceof AxiosError) || error.response?.status !== 401) {
          throw error;
        }

        const renewed = await refreshDeviceSession();
        if (!renewed) {
          clearDeviceSession();
          setLoginError(
            'Device session could not be renewed. Sign in again.'
          );
          return;
        }
        accessToken = renewed;
        scheduleTokenRefresh();
        res = await postClock(blob, accessToken, attendanceLogType);
      }

      stopCamera();
      setPhase({ kind: 'success', result: res.data });
      scheduleReturnHome(4000);
    } catch (error) {
      if (abort.signal.aborted) return;

      if (error instanceof AxiosError && error.response?.status === 401) {
        clearDeviceSession();
        setLoginError('Device session could not be renewed. Sign in again.');
        return;
      }

      const message =
        error instanceof Error && !(error instanceof AxiosError)
          ? error.message
          : error instanceof AxiosError
            ? error.response?.data?.errorMessage ||
              error.response?.data?.message ||
              'Face clock failed'
            : 'Face clock failed';

      stopCamera();
      setPhase({ kind: 'error', message });
      scheduleReturnHome(3500);
    } finally {
      if (blinkAbortRef.current === abort) {
        blinkAbortRef.current = null;
      }
    }
  };

  const attendanceLabel = (value: ClockResult['attendanceLogType']) => {
    if (value === 'TimeOut' || value === 1) return 'Time Out';
    return 'Time In';
  };

  const pageShell: React.CSSProperties = {
    background: '#ffffff',
    color: '#0f172a',
    minHeight: '100vh'
  };

  if (!deviceToken) {
    return (
      <div
        className="min-vh-100 d-flex flex-column align-items-center justify-content-center px-3"
        style={pageShell}
      >
        <Stack gap={3} className="w-100" style={{ maxWidth: 420 }}>
          <div className="text-center">
            <div
              className="text-uppercase fw-semibold text-secondary"
              style={{ letterSpacing: '0.12em' }}
            >
              KJ Auto
            </div>
            <h1 className="h3 mb-1">Face Clock device</h1>
            <p className="mb-0 text-secondary">
              Sign in with the biometrics device account
            </p>
          </div>

          {loginError && <Alert variant="danger">{loginError}</Alert>}

          <Form onSubmit={event => void handleDeviceLogin(event)}>
            <Form.Group className="mb-3 text-start">
              <Form.Label htmlFor="deviceEmail">Email</Form.Label>
              <Form.Control
                id="deviceEmail"
                type="email"
                autoComplete="username"
                value={deviceEmail}
                onChange={e => setDeviceEmail(e.target.value)}
                placeholder={DEFAULT_DEVICE_EMAIL}
              />
            </Form.Group>
            <Form.Group className="mb-3 text-start">
              <Form.Label htmlFor="devicePassword">Password</Form.Label>
              <Form.Control
                id="devicePassword"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Device password"
              />
            </Form.Group>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-100 py-3"
              disabled={loginBusy || !deviceEmail.trim() || !password}
            >
              {loginBusy ? 'Signing in…' : 'Sign in device'}
            </Button>
          </Form>
        </Stack>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100 d-flex flex-column align-items-center justify-content-center px-3"
      style={pageShell}
    >
      <Stack gap={3} className="w-100" style={{ maxWidth: 480 }}>
        <div className="text-center">
          <div
            className="text-uppercase fw-semibold text-secondary"
            style={{ letterSpacing: '0.12em' }}
          >
            KJ Auto
          </div>
          <h1 className="h3 mb-1">Face Clock</h1>
          <p className="mb-0 text-secondary">
            {phase.kind === 'home'
              ? 'Choose Time In or Time Out'
              : phase.kind === 'capture'
                ? `${actionLabel(phase.action)} — blink when ready`
                : phase.kind === 'success'
                  ? 'Done'
                  : 'Something went wrong'}
          </p>
          <p className="mb-0 mt-1 small text-secondary">{deviceEmail}</p>
        </div>

        {phase.kind === 'home' && (
          <div className="d-grid gap-3">
            <Button
              variant="success"
              size="lg"
              className="py-4 fs-3"
              onClick={() => void startAttendance('TimeIn')}
            >
              Time In
            </Button>
            <Button
              variant="warning"
              size="lg"
              className="py-4 fs-3"
              onClick={() => void startAttendance('TimeOut')}
            >
              Time Out
            </Button>
          </div>
        )}

        {phase.kind === 'capture' && (
          <>
            <div
              className="position-relative overflow-hidden rounded-3 border"
              style={{
                aspectRatio: '3 / 4',
                background: '#f8fafc',
                borderColor: '#e2e8f0'
              }}
            >
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="w-100 h-100"
                style={{ objectFit: 'cover', transform: 'scaleX(-1)' }}
              />
              <div
                className="position-absolute top-0 start-0 w-100 px-3 pt-3"
                style={{ pointerEvents: 'none', zIndex: 2 }}
              >
                <div
                  className="rounded-3 px-3 py-3 text-center text-white"
                  style={{
                    background: 'rgba(15, 23, 42, 0.78)',
                    backdropFilter: 'blur(6px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                  }}
                >
                  <div
                    className="fw-semibold"
                    style={{
                      fontSize: '1.45rem',
                      lineHeight: 1.35,
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {phase.prompt}
                  </div>
                  {(phase.prompt.includes('verifying') ||
                    phase.prompt.includes('opening camera') ||
                    phase.prompt.includes('capturing') ||
                    phase.prompt.includes('loading')) && (
                    <Spinner
                      animation="border"
                      variant="light"
                      size="sm"
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="phoenix-secondary"
              size="lg"
              className="py-3"
              onClick={goHome}
            >
              Cancel
            </Button>
          </>
        )}

        {phase.kind === 'success' && (
          <Alert variant="success" className="mb-0 text-center">
            <div className="fs-4 fw-semibold">{phase.result.displayName}</div>
            <div>{attendanceLabel(phase.result.attendanceLogType)}</div>
            <div className="fs-5 fw-semibold">
              {formatAttendanceTime(phase.result.timestamp)}
            </div>
            <div className="small text-body-secondary">
              Score {(phase.result.matchScore * 100).toFixed(1)}%
            </div>
          </Alert>
        )}

        {phase.kind === 'error' && (
          <Alert variant="danger" className="mb-0 text-center">
            {phase.message}
          </Alert>
        )}

        {(phase.kind === 'success' || phase.kind === 'error') && (
          <Button
            variant="primary"
            size="lg"
            className="py-3"
            onClick={goHome}
          >
            Back
          </Button>
        )}

        <Button variant="link" className="text-secondary" onClick={clearDeviceSession}>
          Sign out device
        </Button>
      </Stack>
    </div>
  );
};

export default FaceClock;
